export type GalleryImage = {
  number: number;
  /** Fallback for browsers that ignore `srcset`; also the largest variant. */
  src: string;
  /** Candidate widths, so the browser downloads only what it will display. */
  srcSet: string;
  /** Intrinsic size of `src`, set on the img to reserve layout space. */
  width: number;
  height: number;
  alt: string;
  to: string;
};

/**
 * Single source of truth for the gallery.
 *
 * Gallery and View each hard-coded their own copy of this list, so adding an
 * image meant editing two files and they could silently drift apart. Vite
 * resolves this glob at build time into hashed asset URLs, so dropping a PNG
 * into `assets/images/gallery/` is now all it takes.
 *
 * The masters are ~2500px PNGs of around 1.7MB each. The grid paints them
 * about 400px wide, so serving the master was costing roughly 60x the bytes
 * actually needed. imagetools re-encodes each one to WebP at the widths below
 * and `sizes` at the call site picks the right one. The range runs wide enough
 * to also cover View, which shows a single image near full width.
 */
const modules = import.meta.glob<ImageToolsPicture>(
  "../assets/images/gallery/*.png",
  {
    eager: true,
    import: "default",
    query:
      "?w=400;800;1200;1600;2000&format=webp&quality=75&effort=6&as=picture",
  }
);

export const galleryImages: GalleryImage[] = Object.entries(modules)
  .map(([path, picture]) => {
    const number = Number(path.match(/(\d+)\.png$/)?.[1]);
    return {
      number,
      src: picture.img.src,
      srcSet: picture.sources.webp,
      width: picture.img.w,
      height: picture.img.h,
      alt: `Motocross graphics design ${number}`,
      to: `/view/${number}`,
    };
  })
  .filter(({ number }) => Number.isInteger(number))
  .sort((a, b) => a.number - b.number);

export const GALLERY_IMAGE_COUNT = galleryImages.length;

/** Look up a gallery image by its 1-based number, or undefined if unknown. */
export function getGalleryImage(number: number): GalleryImage | undefined {
  return galleryImages.find((image) => image.number === number);
}
